import React from 'react';
import { Music2 } from 'lucide-react';

type BrandIconProps = {
  platform: string;
  kind: 'social' | 'streaming';
  size?: number; // px size (width/height)
  className?: string;
  title?: string;
  // Optional fallback to render when image is missing or fails
  fallback?: React.ReactNode;
};

// Normalizes keys like "YouTube Music" -> "youtube-music"
const normalize = (s: string) => String(s || '').trim().toLowerCase().replace(/\s+/g, '-');

// Known file names expected in /public/brands (preferred names)
const fileMap: Record<string, string> = {
  // socials
  'tiktok': 'tiktok.png',
  // streaming
  'spotify': 'spotify.png',
  'youtube': 'youtube-music.png',
  'youtube-music': 'youtube-music.png',
  'soundcloud': 'soundcloud.png',
  'deezer': 'deezer.png',
  'audiomack': 'audiomack.png',
  'boomplay': 'boomplay.png',
  'tidal': 'tidal.png',
};

export default function BrandIcon({ platform, kind, size = 16, className = '', title, fallback }: BrandIconProps) {
  const key = normalize(platform);
  // Build candidate filenames to try in order
  const candidates = React.useMemo(() => {
    const preferred = fileMap[key] ? [`/brands/${fileMap[key]}`] : [];
    const base = `/brands/${key}`;
    const extensions = ['.svg', '.png', '.webp', '.jpg', '.jpeg'];
    const generated = extensions.map((ext) => `${base}${ext}`);
    // Deduplicate while preserving order
    const seen = new Set<string>();
    return [...preferred, ...generated].filter((u) => (seen.has(u) ? false : (seen.add(u), true)));
  }, [key]);

  const [idx, setIdx] = React.useState(0);

  // If we ran out of candidates, render fallback
  if (!candidates[idx]) {
    if (fallback) return <>{fallback}</>;
    if (kind === 'streaming') return <Music2 className={className} width={size} height={size} />;
    return null;
  }

  return (
    <img
      src={candidates[idx]}
      onError={() => setIdx((i) => i + 1)}
      alt={title || platform}
      title={title || platform}
      width={size}
      height={size}
      className={`inline-block object-contain ${className}`}
      style={{ width: size, height: size }}
    />
  );
}
